import { supabase } from "./supabase.js";

// The Supabase boundary for history events (design_documents/history.md,
// migrations/2026-10-14-history.sql). Events are keyed by their own uuid (the
// same on every device). Removal is a soft delete (`deleted`), so devices pull
// incrementally by the server-stamped `synced_at` and still see removals.

export function toCloudHistory(e, userId) {
	return {
		id: e.id,
		user_id: userId,
		at: e.at,
		item_id: e.item_id ?? null,
		media_type: e.media_type ?? null,
		title: e.title ?? null,
		cover_url: e.cover_url ?? null,
		route: e.route ?? null,
		kind: e.kind,
		from_value: e.from_value ?? null,
		to_value: e.to_value ?? null,
		detail: e.detail ?? {},
		reconstructed: !!e.reconstructed,
		deleted: !!e.deleted,
		created_at: e.created_at ?? e.at,
		updated_at: e.updated_at ?? e.at,
	};
}

export function fromCloudHistory(row) {
	return {
		id: row.id,
		at: row.at,
		item_id: row.item_id ?? null,
		media_type: row.media_type ?? null,
		title: row.title ?? null,
		cover_url: row.cover_url ?? null,
		route: row.route ?? null,
		kind: row.kind,
		from_value: row.from_value ?? null,
		to_value: row.to_value ?? null,
		detail: row.detail ?? {},
		reconstructed: !!row.reconstructed,
		deleted: !!row.deleted,
		created_at: row.created_at,
		updated_at: row.updated_at,
		_synced: true,
	};
}

// ---------- remote calls ----------

export async function upsertCloudHistory(rows) {
	const list = Array.isArray(rows) ? rows : [rows];
	if (!list.length) return;
	const { error } = await supabase.from("history_events").upsert(list, { onConflict: "id" });
	if (error) throw error;
}

// Everything changed server-side after `since` (ISO; null = everything), oldest
// first, paged. Returns the rows and the newest synced_at seen.
export async function fetchCloudHistorySince(userId, since, { pageSize = 1000 } = {}) {
	const rows = [];
	let cursor = since;
	for (;;) {
		let q = supabase
			.from("history_events")
			.select("*")
			.eq("user_id", userId)
			.order("synced_at", { ascending: true })
			.limit(pageSize);
		if (cursor) q = q.gt("synced_at", cursor);
		const { data, error } = await q;
		if (error) throw error;
		rows.push(...(data ?? []));
		if (!data || data.length < pageSize) break;
		cursor = data[data.length - 1].synced_at;
	}
	const newest = rows.length ? rows[rows.length - 1].synced_at : since;
	return { rows, newest };
}

// ---------- pull watermark (per user, per device) ----------

export const historyWatermarkKey = (uid) => `historyPulledAt:${uid}`;

// Forget where the history pull got to (sign-out clears local history).
export function resetHistoryWatermark(userId) {
	try {
		if (userId) localStorage.removeItem(historyWatermarkKey(userId));
		else {
			for (let i = localStorage.length - 1; i >= 0; i--) {
				const k = localStorage.key(i);
				if (k?.startsWith("historyPulledAt:")) localStorage.removeItem(k);
			}
		}
	} catch {
		// nothing stored
	}
}
